// API endpoint for backend email service
const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:3001'

/**
 * Send email with optional attachment via backend API
 * @param {Object} emailData - Email data object
 * @param {string} emailData.to - Recipient email address
 * @param {string} emailData.from - Sender email address (must be verified in SendGrid)
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.text - Plain text content
 * @param {string} emailData.html - HTML content
 * @param {Array} emailData.attachments - Array of attachment objects
 * @returns {Promise<Object>} - Result object with success status and message
 */
export async function sendEmail(emailData) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(emailData)
    })

    const result = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Failed to send email'
      }
    }

    return {
      success: true,
      message: result.message || 'Email sent successfully',
      response: result
    }
  } catch (error) {
    console.error('Error sending email:', error)
    return {
      success: false,
      error: error.message || 'Failed to send email. Please check your connection.'
    }
  }
}

/**
 * Send health report email with PDF attachment
 * @param {string} to - Recipient email
 * @param {Object} reportData - Report data
 * @param {string} pdfContent - Base64 encoded PDF content
 * @returns {Promise<Object>} - Result object
 */
export async function sendHealthReport(to, reportData, pdfContent) {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .header { background-color: #e91e63; color: white; padding: 20px; text-align: center; }
        .content { padding: 20px; }
        .footer { background-color: #f5f5f5; padding: 10px; text-align: center; font-size: 12px; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>FemWellness Health Report</h1>
      </div>
      <div class="content">
        <h2>Hello ${reportData.userName},</h2>
        <p>Your health report for ${reportData.reportDate} is ready!</p>
        <p>Please find your detailed health analysis report attached to this email.</p>
        <h3>Report Summary:</h3>
        <ul>
          <li>Report Type: ${reportData.reportType || 'General Health'}</li>
          <li>Generated On: ${reportData.reportDate}</li>
          <li>Status: ${reportData.status || 'Complete'}</li>
        </ul>
        <p>If you have any questions about your report, please don't hesitate to contact your healthcare provider.</p>
      </div>
      <div class="footer">
        <p>&copy; 2024 FemWellness. All rights reserved.</p>
        <p>This is an automated message. Please do not reply to this email.</p>
      </div>
    </body>
    </html>
  `

  const textContent = `
    FemWellness Health Report

    Hello ${reportData.userName},

    Your health report for ${reportData.reportDate} is ready!

    Please find your detailed health analysis report attached to this email.

    Report Summary:
    - Report Type: ${reportData.reportType || 'General Health'}
    - Generated On: ${reportData.reportDate}
    - Status: ${reportData.status || 'Complete'}

    If you have any questions about your report, please don't hesitate to contact your healthcare provider.

    © 2024 FemWellness. All rights reserved.
  `

  return await sendEmail({
    to,
    subject: `Your FemWellness Health Report - ${reportData.reportDate}`,
    text: textContent,
    html: htmlContent,
    attachments: pdfContent ? [{
      content: pdfContent,
      filename: `health-report-${reportData.reportDate}.pdf`,
      type: 'application/pdf',
      disposition: 'attachment'
    }] : []
  })
}

/**
 * Send consultation reminder email
 * @param {string} to - Recipient email
 * @param {Object} consultationData - Consultation details
 * @returns {Promise<Object>} - Result object
 */
export async function sendConsultationReminder(to, consultationData) {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .header { background-color: #e91e63; color: white; padding: 20px; text-align: center; }
        .content { padding: 20px; }
        .appointment-box { background-color: #f5f5f5; padding: 15px; border-left: 4px solid #e91e63; margin: 20px 0; }
        .button { background-color: #e91e63; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 10px; }
        .footer { background-color: #f5f5f5; padding: 10px; text-align: center; font-size: 12px; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Consultation Reminder</h1>
      </div>
      <div class="content">
        <h2>Hello ${consultationData.patientName},</h2>
        <p>This is a reminder about your upcoming consultation.</p>
        <div class="appointment-box">
          <h3>Appointment Details:</h3>
          <p><strong>Doctor:</strong> ${consultationData.doctorName}</p>
          <p><strong>Date:</strong> ${consultationData.date}</p>
          <p><strong>Time:</strong> ${consultationData.time}</p>
          <p><strong>Type:</strong> ${consultationData.type || 'General Consultation'}</p>
        </div>
        <p>Please make sure to arrive 10 minutes before your appointment time.</p>
        <a href="${consultationData.joinLink || '#'}" class="button">Join Consultation</a>
      </div>
      <div class="footer">
        <p>&copy; 2024 FemWellness. All rights reserved.</p>
      </div>
    </body>
    </html>
  `

  return await sendEmail({
    to,
    subject: `Reminder: Your consultation with ${consultationData.doctorName}`,
    html: htmlContent,
    text: `Consultation Reminder\n\nDoctor: ${consultationData.doctorName}\nDate: ${consultationData.date}\nTime: ${consultationData.time}`
  })
}

/**
 * Send welcome email to new users
 * @param {string} to - Recipient email
 * @param {string} userName - User's name
 * @returns {Promise<Object>} - Result object
 */
export async function sendWelcomeEmail(to, userName) {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .header { background-color: #e91e63; color: white; padding: 20px; text-align: center; }
        .content { padding: 20px; }
        .features { display: flex; flex-wrap: wrap; margin: 20px 0; }
        .feature { flex: 1; min-width: 200px; padding: 10px; }
        .button { background-color: #e91e63; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 10px; }
        .footer { background-color: #f5f5f5; padding: 10px; text-align: center; font-size: 12px; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Welcome to FemWellness! 💗</h1>
      </div>
      <div class="content">
        <h2>Hello ${userName},</h2>
        <p>Welcome to FemWellness - Your Complete Women's Health Journey!</p>
        <p>We're excited to have you join our community dedicated to women's health and wellness.</p>
        <h3>What you can do with FemWellness:</h3>
        <div class="features">
          <div class="feature">
            <h4>🏃‍♀️ Fitness & Lifestyle</h4>
            <p>Track your fitness goals and maintain a healthy lifestyle</p>
          </div>
          <div class="feature">
            <h4>🩺 Health Consultation</h4>
            <p>Connect with healthcare professionals for personalized advice</p>
          </div>
          <div class="feature">
            <h4>👥 Community</h4>
            <p>Share experiences and connect with other women</p>
          </div>
        </div>
        <a href="${process.env.VUE_APP_BASE_URL || 'http://localhost:8080'}" class="button">Get Started</a>
      </div>
      <div class="footer">
        <p>&copy; 2024 FemWellness. All rights reserved.</p>
      </div>
    </body>
    </html>
  `

  return await sendEmail({
    to,
    subject: 'Welcome to FemWellness! 💗',
    html: htmlContent,
    text: `Welcome to FemWellness, ${userName}!\n\nWe're excited to have you join our community dedicated to women's health and wellness.`
  })
}

/**
 * Send menstrual records CSV via email
 * @param {string} to - Recipient email
 * @param {string} userName - User's name
 * @param {string} csvContent - Base64 encoded CSV content
 * @param {string} filename - CSV filename
 * @returns {Promise<Object>} - Result object
 */
export async function sendMenstrualRecordsCSV(to, userName, csvContent, filename) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/send-menstrual-records`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        to,
        userName,
        csvContent,
        filename
      })
    })

    const result = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Failed to send email'
      }
    }

    return {
      success: true,
      message: result.message || 'Email sent successfully',
      response: result
    }
  } catch (error) {
    console.error('Error sending menstrual records email:', error)
    return {
      success: false,
      error: error.message || 'Failed to send email. Please check your connection.'
    }
  }
}

export default {
  sendEmail,
  sendHealthReport,
  sendConsultationReminder,
  sendWelcomeEmail,
  sendMenstrualRecordsCSV
}
